import { useMediaQuery } from "@chakra-ui/react";
import { Trans } from "react-i18next";
import { Box } from "@revoo/design-system/ui/Box/Box";
import { Button } from "@revoo/design-system/ui/Button/Button";
import { Flex } from "@revoo/design-system/ui/Flex/Flex";
import { Field } from "@revoo/design-system/ui/Form/Field/Field";
import { Input } from "@revoo/design-system/ui/Form/Input/Input";
import { InputPsw } from "@revoo/design-system/ui/Form/InputPsw/InputPsw";
import { Header3 } from "@revoo/design-system/ui/Typography/Header3";
import { Text1 } from "@revoo/design-system/ui/Typography/Text1";
import { useTheme } from "@revoo/design-system/providers/ThemeProvider";

import { useFormContext } from "react-hook-form";
import Link from "next/link";
import React, { FormEvent, useEffect } from "react";
import { useAnalyticsContext } from "@/libs/shared/analytics/AnalyticsProvider";
import { Analytics } from "@/libs/shared/analytics/Analytics";
import { useLogin } from "@/libs/auth/pages/Login/useLogin";
import { useDisclosureContext } from "@/libs/shared/providers/DisclosureProvider";
import LoginModal from "@/libs/auth/components/LoginModal/LoginModal";
import { LoginModalProvider } from "@/libs/auth/components/LoginModal/LoginModalProvieder";
import { LoginPayloadModel } from "@/libs/auth/redux/auth.types";

export function FormLogin() {
  const { device } = useTheme();
  const [isLargerThanMd] = useMediaQuery(device.md, { ssr: false });
  const isMobileLoginHeader = !isLargerThanMd;

  const analytics = useAnalyticsContext();
  const { loginHandler, isLoading, deletionPending } = useLogin();
  const { onOpen } = useDisclosureContext();

  useEffect(() => {
    if (deletionPending) {
      onOpen();
    }
  }, [deletionPending, onOpen]);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid },
  } = useFormContext();

  const handleConfirmDeletion = () => {
    const values = getValues() as LoginPayloadModel;
    void loginHandler({ ...values, cancel_deletion_request: true });
  };

  return (
    <LoginModalProvider onLoginWithCancel={handleConfirmDeletion}>
      <Box
        as="form"
        onSubmit={(event: FormEvent<HTMLFormElement>) =>
          void handleSubmit(loginHandler as any)(event)
        }
        w="full"
      >
        <Box mb={6} textAlign={isMobileLoginHeader ? "center" : "start"}>
          <Header3 mb={isMobileLoginHeader ? 2 : 0} fontWeight="700">
            <Trans
              i18nKey={
                isMobileLoginHeader ? "Login:title_mobile" : "Login:title"
              }
            />
          </Header3>
          {isMobileLoginHeader ? (
            <Text1
              fontSize="xl"
              fontWeight={600}
              color="palette.secondary.0"
            >
              <Trans i18nKey="Login:subtitle_mobile" />
            </Text1>
          ) : null}
        </Box>
        <Field
          component={Input as any}
          label={<Trans i18nKey={"Login:email"} />}
          name="email"
          control={control as any}
          wrapProps={{
            mb: 8,
          }}
        />
        <Field
          component={InputPsw}
          label={<Trans i18nKey={"Login:password"} />}
          name="password"
          control={control as any}
          wrapProps={{
            mb: 4,
          }}
        />
        <Flex mb={10} justifyContent="flex-end">
          <Link href={"/recover-password"}>
            <Text1 fontWeight={500}>
              <Trans i18nKey="RecoverPassword:recover_password" />
            </Text1>
          </Link>
        </Flex>
        <Flex flexDir="column">
          <Analytics
            analytics={analytics}
            events={{ name: "action-login" }}
            component={Button}
            type="submit"
            variant="solid"
            isLoading={isLoading}
            isDisabled={!isValid}
            size={"lg"}
          >
            <Trans i18nKey="Login:login" />
          </Analytics>
        </Flex>
        <LoginModal />
      </Box>
    </LoginModalProvider>
  );
}
